import { useGetUrl } from '@seek/ca-myprofile-utils';
import { useHubble } from '@seek/hubble';
import { useTranslations } from '@vocab/react';
import {
  Text,
  Drawer,
  TextLink,
  Stack,
  Inline,
  Button,
} from 'braid-design-system';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { CardError } from 'src/components/Card/CardError/CardError';
import { PROFILE_VISIBILITY } from 'src/components/Router';
import { useEnterReadMode } from 'src/components/Router/useEnterReadMode';
import { useAppConfig } from 'src/modules/appConfig';
import { hotjarEvent } from 'src/modules/hotjar';
import { useRetryAction } from 'src/modules/retry-actions/hooks';

import translations from './.vocab';
import { EditProfileVisibilitySettings } from './EditProfileVisibilitySettings';
import { profileVisibilityDrawerOpened } from './actionCreators';

interface FormProps {
  isOpen: boolean;
  personalDetailsLoading: boolean;
}

export const EditProfileVisibility = ({
  isOpen,
  personalDetailsLoading,
}: FormProps) => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const { zone, language } = useAppConfig();
  const getUrl = useGetUrl(zone, language);
  const retryAction = useRetryAction(PROFILE_VISIBILITY);
  const showErrorAlert = Boolean(retryAction?.canRetry);
  const enterReadMode = useEnterReadMode();

  useEffect(() => {
    hotjarEvent('profile-visibility-drawer-opened');
  }, []);

  useEffect(() => {
    if (isOpen && !personalDetailsLoading) {
      dispatch(profileVisibilityDrawerOpened());
    }
  }, [isOpen, personalDetailsLoading, dispatch]);

  const hubble = useHubble();

  const trackLearnMoreBtnClick = () => {
    hubble.trackEvent('component_pressed', {
      component: {
        type: 'textLink',
        name: 'learn more about visibility',
      },
      displayLanguage: language,
      zone,
    });
  };

  return (
    <Drawer
      id="profile-visibility-settings"
      data={{ automation: 'profile-visibility-settings' }}
      title={t('Profile visibility')}
      description={
        <Text tone="secondary">
          {t('For all settings, your profile including...', {
            TextLink: (children: React.ReactNode[]) => (
              <TextLink
                key="privacyPolicyPath"
                target="_blank"
                onClick={trackLearnMoreBtnClick}
                href={getUrl('profile-privacy')}
                hitArea="large"
                weight="weak"
                data={{ testid: 'learn-more-privacy-policy' }}
              >
                {children}
              </TextLink>
            ),
          })}
        </Text>
      }
      open={isOpen}
      onClose={enterReadMode}
      closeLabel={t('Close')}
      width="small"
    >
      {showErrorAlert && <CardError card={PROFILE_VISIBILITY} />}
      <Stack space="large">
        <EditProfileVisibilitySettings />
        <Inline space="small" collapseBelow="tablet">
          <Button
            id="done-btn"
            tone="formAccent"
            data={{ automation: 'done-btn' }}
            onClick={enterReadMode}
          >
            {t('Done')}
          </Button>
        </Inline>
      </Stack>
    </Drawer>
  );
};
